import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RoomAudioRenderer, StartAudio, useRoomContext } from "@livekit/components-react";
import { useToolResults } from "../../useToolResults";
import { AgentHeader } from "../AgentHeader/AgentHeader";
import { AvatarVideo } from "../AvatarVideo/AvatarVideo";
import { ConversationFeedback } from "../ConversationFeedback/ConversationFeedback";
import { ErrorBanner } from "../ErrorBanner/ErrorBanner";
import { ReconnectStatus } from "../ErrorBanner/ReconnectStatus";
import { TRANSCRIPT_RPC_METHOD, buildTranscript, type TranscriptTurn } from "../agentRejoin";
import { MessageInput } from "../MessageInput/MessageInput";
import { MessageList } from "../MessageList/MessageList";
import { useCallMachine, type NextConversation } from "../useCallMachine";
import { useChatTransport } from "../useChatTransport";
import { McpAppHostContext } from "../../cards/mcp-app/host";
import { showStarterChips } from "../StarterChips/StarterChips";
import { isAgentReady, shouldQueueSend } from "../queuedSend";
import { shouldShowNewChat } from "../newChat";
import { greetingText, shouldShowLocalGreeting } from "../greeting";
import { useTypedGreeting } from "../typedGreeting";

export function AgentPanel({
  serverUrl,
  avatarUrl,
  userInitials,
  onClose,
  closeLabel,
  ensureSession,
  endSession,
  sessionStarted,
  onConversationEnded,
  setInitialMessage,
  panelExpanded,
  onToggleExpand,
  onSetExpanded,
  onVoiceStateChange,
  startCallSignal,
  initialMessage,
  requestAgentRejoin,
  greetingName,
}: {
  serverUrl: string;
  avatarUrl?: string;
  userInitials?: string | null;
  onClose: () => void;
  closeLabel?: string;
  ensureSession: (withMic: boolean) => Promise<void>;
  endSession: () => Promise<void>;
  sessionStarted: boolean;
  onConversationEnded: (next?: NextConversation) => void;
  setInitialMessage: (msg: string) => void;
  panelExpanded: boolean;
  onToggleExpand: () => void;
  onSetExpanded: (expanded: boolean) => void;
  onVoiceStateChange?: (active: boolean) => void;
  startCallSignal?: number;
  initialMessage?: string;
  requestAgentRejoin?: (transcript: TranscriptTurn[], voice: boolean) => Promise<void>;
  // Visitor's first name for the instant greeting (same source as the token).
  greetingName?: string | null;
}) {
  const room = useRoomContext();
  // Latest on-screen thread, for the transcript handed to a rejoining agent.
  // Written after useChatTransport below; read only when a rejoin fires.
  const messagesRef = useRef<ReturnType<typeof useChatTransport>["state"]["messages"]>([]);
  const avatarVideoBg = `${serverUrl}/assets/courtney-avatar-full.webp`;
  const call = useCallMachine({
    sessionStarted,
    ensureSession,
    endSession,
    onConversationEnded,
    requestAgentRejoin,
    getTranscript: () => buildTranscript(messagesRef.current, room.localParticipant.identity),
  });
  const chat = useChatTransport({
    sessionStarted,
    ensureSession,
    reportError: call.actions.setError,
    setInitialMessage,
  });
  const { reconnecting, conversationEnded, endedBy, conversationId, voiceModeUsed, callError, agentJoined, inVoiceMode, callPending, showVisualizer, avatarVideoTrack, agentIdentity } = call.state;

  // A Courtney re-delivered after a worker crash asks for the conversation so
  // far (src/resume.py request_transcript). Latest-value ref for voice mode.
  const inVoiceModeRef = useRef(inVoiceMode);
  inVoiceModeRef.current = inVoiceMode;
  useEffect(() => {
    room.registerRpcMethod(TRANSCRIPT_RPC_METHOD, async () =>
      JSON.stringify({
        transcript: buildTranscript(messagesRef.current, room.localParticipant.identity),
        voice: inVoiceModeRef.current,
      }),
    );
    return () => {
      room.unregisterRpcMethod(TRANSCRIPT_RPC_METHOD);
    };
  }, [room]);

  // Mobile embed only: report live voice-call state to the native host so the
  // minimized pill can show/hide its orange call button. No-op on the floating
  // web widget (prop is undefined there).
  useEffect(() => {
    onVoiceStateChange?.(inVoiceMode);
  }, [inVoiceMode, onVoiceStateChange]);

  // Mobile embed only: the native pill's phone button bumps startCallSignal to
  // ask for voice mode. Latest-value ref so the effect depends only on the
  // signal — the call machine is rebuilt every render. startVoiceCall itself
  // no-ops when a call is already live or pending.
  const startVoiceCallRef = useRef(call.actions.startVoiceCall);
  startVoiceCallRef.current = call.actions.startVoiceCall;
  useEffect(() => {
    if (!startCallSignal) return;
    void startVoiceCallRef.current();
  }, [startCallSignal]);

  // Mounting this panel IS opening the chat (desktop: first open of the
  // launcher; /mobile embed and native webview: page load). Start the session
  // right away so the agent joins and greets. Layout effect so the typing
  // indicator paints on the first frame. Mount-only on purpose. Two other
  // mounts start the session themselves and must not race a second one:
  // something typed/tapped on the previous conversation's ended screen
  // (initialMessage: the fresh chat opens by sending it) and the native
  // phone button (startCallSignal: starts voice). ensureSession is also
  // re-entrancy guarded, so a double call can never create two sessions.
  useLayoutEffect(() => {
    if (initialMessage) void chat.actions.startChatWithMessage(initialMessage);
    else if (!startCallSignal) void chat.actions.startChat();
  }, []);

  const { messages, isSending, showConnecting } = chat.state;
  messagesRef.current = messages;
  // useSessionMessages is room-scoped — session.end() disconnects the room and
  // empties `messages`. Capture the live transcript each render while the
  // conversation is active (ref write during render is the "latest value"
  // pattern, no re-render), then freeze it once the call ends so it stays on
  // screen until the user starts a new chat (which remounts and resets this).
  const frozenMessagesRef = useRef(messages);
  if (!conversationEnded) frozenMessagesRef.current = messages;
  const displayedMessages = conversationEnded ? frozenMessagesRef.current : messages;
  // Session is live but she hasn't greeted yet — show the thinking dots through
  // the whole connect→room-made→greeting window (not just the brief websocket
  // connect), so the indicator doesn't vanish while we wait for her first line.
  // Show her greeting the instant the chat opens instead of waiting 1–3 s for
  // the agent to join; her own identical line replaces it in place.
  const agentHasSpoken = displayedMessages.some(
    (m) => !(m.type === "userTranscript" || m.from?.identity === room.localParticipant.identity),
  );
  const localGreeting = shouldShowLocalGreeting({
    openedWithMessage: !!initialMessage,
    openedWithCall: !!startCallSignal,
    conversationEnded,
    agentHasSpoken,
  })
    ? greetingText(greetingName)
    : null;
  // Play it like her other replies: dots for a beat, then the words stream in.
  const typedGreeting = useTypedGreeting(localGreeting);
  const greetingDisplay =
    typedGreeting.phase === "dots" ? "" : typedGreeting.phase === "typing" ? typedGreeting.shown : undefined;
  const awaitingGreeting =
    typedGreeting.phase === "dots" ||
    (sessionStarted && displayedMessages.length === 0 && !conversationEnded && !localGreeting);
  // Lines typed/tapped before the agent has joined. Held here (shown as user
  // bubbles) and sent once it has; never a second session.
  const [queued, setQueued] = useState<string[]>([]);
  const agentReady = isAgentReady({ agentJoined, agentState: call.state.agentState });
  const submitText = async (text: string) => {
    if (conversationEnded) return call.actions.startNewChat({ message: text });
    // The agent left an idle chat: this send brings it back (rejoin with the
    // transcript) and waits in the queue until it is in the room.
    call.actions.wakeFromIdle();
    if (shouldQueueSend({ sessionStarted, agentJoined: agentReady })) {
      setQueued((q) => [...q, text]);
      return;
    }
    return chat.actions.sendMessage(text);
  };
  const flushingRef = useRef(false);
  useEffect(() => {
    if (queued.length === 0) return;
    if (!sessionStarted) {
      // The start failed (error banner is up): drop what was waiting rather than
      // silently retrying a send.
      setQueued([]);
      return;
    }
    if (!agentReady || flushingRef.current) return;
    flushingRef.current = true;
    const batch = queued;
    void (async () => {
      let sent = 0;
      try {
        for (const text of batch) {
          await chat.actions.sendMessage(text);
          sent += 1;
          setQueued((q) => q.slice(1)); // its real bubble replaces the pending one
        }
      } catch {
        // sendMessage already surfaced the error; drop the rest of the batch
        setQueued((q) => q.slice(batch.length - sent));
      } finally {
        flushingRef.current = false;
      }
    })();
  }, [queued, sessionStarted, agentReady]);

  // MCP Apps cards (cards/mcp-app) fetch their HTML from this server, and a card's
  // "send a message" (e.g. tapping a time slot) goes out as the visitor's text.
  const mcpAppHost = { serverUrl, sendText: submitText };

  // Chips: from the moment the chat opens until the user does anything.
  const userHasActed =
    !!initialMessage ||
    queued.length > 0 ||
    inVoiceMode ||
    voiceModeUsed ||
    displayedMessages.some(
      (m) => m.type === "userTranscript" || m.from?.identity === room.localParticipant.identity,
    );
  const starterChips = showStarterChips({ conversationEnded, userHasActed }) && typedGreeting.phase === "done"
    ? { onPick: (message: string) => void submitText(message) }
    : undefined;
  const toolResults = useToolResults();
  const [videoVisible, setVideoVisible] = useState(false);
  const [avatarRequested, setAvatarRequested] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const avatarRetryRef = useRef(0);

  // Reset video state when the call ends (conversation ends or voice mode drops)
  useEffect(() => {
    if (conversationEnded || !inVoiceMode) {
      setVideoVisible(false);
      setAvatarRequested(false);
      setAvatarError(null);
      avatarRetryRef.current = 0;
    }
  }, [conversationEnded, inVoiceMode]);

  const showingVideo = !!(videoVisible && avatarRequested && inVoiceMode && !conversationEnded);

  // Timeout: if avatar track hasn't appeared within 15s, auto-retry once then show error
  useEffect(() => {
    if (!showingVideo || avatarVideoTrack || avatarError) return;
    const timeout = setTimeout(() => {
      if (avatarRetryRef.current < 1) {
        avatarRetryRef.current += 1;
        setAvatarRequested(false);
        setTimeout(() => requestAvatar(), 500);
      } else {
        setAvatarError("Video didn't connect — tap to retry");
      }
    }, 15_000);
    return () => clearTimeout(timeout);
  }, [showingVideo, avatarVideoTrack, avatarError]);

  // Clear error once the track actually arrives
  useEffect(() => {
    if (avatarVideoTrack && avatarError) setAvatarError(null);
  }, [avatarVideoTrack, avatarError]);

  const requestAvatar = useCallback(async () => {
    if (avatarRequested) return;
    if (!agentIdentity) {
      setAvatarError("Agent not connected — tap to retry");
      return;
    }
    setAvatarRequested(true);
    setAvatarError(null);
    try {
      await room.localParticipant.performRpc({
        destinationIdentity: agentIdentity,
        method: "enableAvatar",
        payload: "",
      });
    } catch (err) {
      console.error("[lk-widget] enableAvatar RPC failed:", err);
      setAvatarRequested(false);
      setAvatarError("Couldn't start video — tap to retry");
    }
  }, [avatarRequested, agentIdentity, room]);

  const disableAvatar = useCallback(async () => {
    if (!avatarRequested) return;
    setAvatarRequested(false);
    if (!agentIdentity) return;
    try {
      await room.localParticipant.performRpc({
        destinationIdentity: agentIdentity,
        method: "disableAvatar",
        payload: "",
      });
    } catch {
      // best-effort
    }
  }, [avatarRequested, agentIdentity, room]);

  const retryAvatar = useCallback(() => {
    setAvatarError(null);
    avatarRetryRef.current = 0;
    setAvatarRequested(false);
    requestAvatar();
  }, [requestAvatar]);

  const toggleVideo = useCallback(async () => {
    if (videoVisible) {
      setVideoVisible(false);
      onSetExpanded(false);
      setAvatarError(null);
      avatarRetryRef.current = 0;
      disableAvatar();
      return;
    }
    setVideoVisible(true);
    onSetExpanded(true);
    if (!avatarRequested && agentJoined) {
      requestAvatar();
    }
  }, [videoVisible, avatarRequested, agentJoined, onSetExpanded, disableAvatar, requestAvatar]);

  return (
    <McpAppHostContext.Provider value={mcpAppHost}>
    <div className="lk-widget-agent">
      {showVisualizer && <RoomAudioRenderer />}
      {/* Auto-renders only when the browser blocks audio playback. Voice only:
          the chat now connects on open with no user gesture, and a text-only
          user must not be told to "enable audio". */}
      {(inVoiceMode || voiceModeUsed) && <StartAudio label="Tap to enable audio" />}

      <AgentHeader
        avatarUrl={avatarUrl}
        onClose={onClose}
        closeLabel={closeLabel}
        panelExpanded={panelExpanded}
        onToggleExpand={onToggleExpand}
        onAvatarClick={inVoiceMode && agentJoined ? toggleVideo : undefined}
        videoActive={showingVideo}
        onNewChat={
          shouldShowNewChat({ userHasActed, conversationEnded })
            ? () => void call.actions.startNewChatNow()
            : undefined
        }
        newChatConfirm={inVoiceMode && !conversationEnded}
      />

      {conversationEnded && voiceModeUsed && <div className="lk-widget-ended-strip">Call ended</div>}

      {showingVideo ? (
        <div className="lk-widget-split">
          <div className="lk-widget-split-video">
            {avatarVideoTrack ? (
              <AvatarVideo trackRef={avatarVideoTrack} onClose={toggleVideo} />
            ) : avatarError ? (
              <div className="lk-widget-avatar-loading lk-widget-avatar-error" onClick={retryAvatar}>
                <div className="lk-widget-avatar-loading-bg" style={{ backgroundImage: `url(${avatarVideoBg})` }} />
                <span className="lk-widget-avatar-loading-text">{avatarError}</span>
              </div>
            ) : (
              <div className="lk-widget-avatar-loading">
                <div className="lk-widget-avatar-loading-bg" style={{ backgroundImage: `url(${avatarVideoBg})` }} />
                <span className="lk-widget-avatar-loading-text">Starting video...</span>
              </div>
            )}
          </div>
          <div className="lk-widget-split-chat">
            <MessageList
              messages={displayedMessages}
              connecting={showConnecting}
              userInitials={userInitials}
              avatarUrl={avatarUrl}
              toolResults={toolResults}
              agentState={call.state.agentState}
              initialLoading={awaitingGreeting}
              starterChips={starterChips}
              pendingMessages={queued}
              localGreeting={localGreeting}
              greetingDisplay={greetingDisplay}
            />
            {reconnecting && !conversationEnded && <ReconnectStatus />}
            {callError && !reconnecting && (
              <ErrorBanner
                message={callError}
                onRetry={!sessionStarted ? call.actions.retryAfterError : undefined}
              />
            )}
            <MessageInput
              onSubmit={submitText}
              onToggleVoice={call.actions.toggleVoice}
              voiceActive={inVoiceMode}
              voiceConnecting={callPending}
              isSending={isSending}
              showVisualizer={showVisualizer}
              agentState={call.state.agentState}
              microphoneTrack={call.state.microphoneTrack}
            />
          </div>
        </div>
      ) : (
        <>
          <MessageList
            messages={displayedMessages}
            connecting={showConnecting}
            userInitials={userInitials}
            avatarUrl={avatarUrl}
            toolResults={toolResults}
            agentState={call.state.agentState}
            initialLoading={awaitingGreeting}
            starterChips={starterChips}
            pendingMessages={queued}
            localGreeting={localGreeting}
            greetingDisplay={greetingDisplay}
            footer={
              endedBy && (
                <ConversationFeedback
                  serverUrl={serverUrl}
                  conversationId={conversationId}
                  endedBy={endedBy}
                  channel={voiceModeUsed ? "voice" : "chat"}
                  onStartNew={(message) => call.actions.startNewChat({ message })}
                  onStartFresh={() => call.actions.startNewChat()}
                />
              )
            }
          />

          {reconnecting && !conversationEnded && <ReconnectStatus />}
          {callError && !conversationEnded && !reconnecting && (
            <ErrorBanner
              message={callError}
              onRetry={!sessionStarted ? call.actions.retryAfterError : undefined}
            />
          )}

          {/* The composer is always there. Before the first send it is also the
              way in: sending or tapping the mic starts the session (nothing
              connects on open). After the conversation ends, sending or tapping
              call starts a fresh session instead of the dead one. */}
          <MessageInput
            onSubmit={
              conversationEnded
                ? async (text) => call.actions.startNewChat({ message: text })
                : submitText
            }
            onToggleVoice={
              conversationEnded
                ? () => call.actions.startNewChat({ voice: true })
                : call.actions.toggleVoice
            }
            voiceActive={inVoiceMode}
            voiceConnecting={callPending}
            isSending={isSending}
            showVisualizer={showVisualizer}
            agentState={call.state.agentState}
            microphoneTrack={call.state.microphoneTrack}
          />
        </>
      )}
    </div>
    </McpAppHostContext.Provider>
  );
}
